/**
 * domains/market/engines/setup2/adapter.ts — MER Algo Footprints v7.1H
 *
 * Adapter: RichSetup → TradeSetup (legacy shared type)
 *
 * Contract:
 *   - NO_VALID_SETUP is an explicit evaluated outcome, NOT absence of data.
 *   - It produces a TradeSetup with confidence=0, maturity='developing',
 *     and type='[NO_VALID_SETUP]' so legacy consumers can distinguish it
 *     from a low-scoring real setup.
 *   - richSetupsToTradeSetups() always includes the NVS entry if present —
 *     callers should filter by type !== '[NO_VALID_SETUP]' if they want only
 *     tradable setups.
 *   - richSetupsToTradeSetupsFiltered() excludes NVS for consumers that only
 *     want actionable candidates.
 */

import { TradeSetup, SetupMaturity, SetupAlignment } from '../../../../shared/types';
import { RichSetup, SetupState, SetupDirection } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function stateToMaturity(state: SetupState): SetupMaturity {
  if (state === 'READY' || state === 'ACTIVE') return 'actionable';
  if (state === 'DEVELOPING')                  return 'confirmed';
  return 'developing';
}

function directionToAlignment(
  direction:   SetupDirection,
  trend_dir:   string | undefined,
  setup_state: SetupState,
): SetupAlignment {
  if (!trend_dir || trend_dir === 'neutral') return 'aligned';
  const counter = (direction === 'LONG'  && trend_dir === 'bearish') ||
                  (direction === 'SHORT' && trend_dir === 'bullish');
  if (counter)
    return setup_state === 'READY' || setup_state === 'ACTIVE'
      ? 'countertrend-confirmed'
      : 'countertrend-unconfirmed';
  return 'aligned';
}

// ─── NO_VALID_SETUP sentinel value ────────────────────────────────────────────
// This type prefix makes it unambiguous in legacy TradeSetup arrays.
// Consumers can check: setup.type.startsWith('[NO_VALID_SETUP]')
const NVS_TYPE_PREFIX = '[NO_VALID_SETUP]';

export function isNoValidSetupTradeSetup(ts: TradeSetup): boolean {
  return ts.type.startsWith(NVS_TYPE_PREFIX);
}

// ─── Main adapter ─────────────────────────────────────────────────────────────

export function richSetupToTradeSetup(rich: RichSetup): TradeSetup {
  const isNVS = rich.setup_classification === 'NO_VALID_SETUP';

  if (isNVS) {
    // Explicit evaluated no-trade outcome — preserve semantic explicitly
    const reason = rich.conflicting_factors[0] ?? 'No valid setup in current conditions';
    return {
      type:          `${NVS_TYPE_PREFIX} ${reason}`,
      dir:           'either',
      entry:         'N/A — no valid setup',
      confirmation:  'N/A',
      invalidation:  'N/A',
      t1:            '',
      t2:            '',
      rationale:     reason,
      regimeFit:     'NO_VALID_SETUP — evaluation completed, no trade',
      confidence:    0,
      entryPrice:    null,
      stopPrice:     null,
      target1Price:  null,
      target2Price:  null,
      maturity:      'developing',   // lowest — never 'actionable'
      alignment:     'aligned',
      stateTransitionConfirmed: false,
      countertrend:  false,
      confirmationMissing: rich.actionability.missing_items,
    };
  }

  const dir: 'long' | 'short' | 'either' =
    rich.direction === 'LONG'  ? 'long'
    : rich.direction === 'SHORT' ? 'short'
    : 'either';

  const trendDir = rich.audit.signal_inputs['trend_dir'] as string | undefined;

  return {
    type:          rich.setup_name,
    dir,
    entry:         rich.entry_model.trigger,
    confirmation:  rich.entry_model.confirmation,
    invalidation:  rich.risk_model.invalidation_event,
    t1:            rich.targets.target_1?.toString() ?? '',
    t2:            rich.targets.target_2?.toString() ?? '',
    rationale:     rich.supporting_factors.join(' | ') || rich.setup_name,
    regimeFit:     `${rich.setup_classification} — score ${rich.setup_score}`,
    confidence:    rich.setup_score,
    entryPrice:    rich.entry_model.entry_price_ideal,
    stopPrice:     rich.risk_model.stop_level,
    target1Price:  rich.targets.target_1,
    target2Price:  rich.targets.target_2,
    maturity:      stateToMaturity(rich.setup_state),
    alignment:     directionToAlignment(rich.direction, trendDir, rich.setup_state),
    stateTransitionConfirmed: rich.setup_state === 'READY'     ||
                              rich.setup_state === 'ACTIVE'    ||
                              rich.setup_state === 'DEVELOPING',
    countertrend:  rich.conflicting_factors.some(f => f.toLowerCase().includes('counter')),
    confirmationMissing: rich.actionability.missing_items,
  };
}

/** Convert all RichSetups → TradeSetup[], including NVS sentinel. */
export function richSetupsToTradeSetups(richSetups: RichSetup[]): TradeSetup[] {
  return richSetups.map(richSetupToTradeSetup);
}

/**
 * Convert RichSetups → TradeSetup[], excluding NO_VALID_SETUP entries.
 * Use this when callers only want tradable candidates.
 */
export function richSetupsToTradeSetupsFiltered(richSetups: RichSetup[]): TradeSetup[] {
  return richSetups
    .filter(r => r.setup_classification !== 'NO_VALID_SETUP')
    .map(richSetupToTradeSetup);
}

/**
 * Returns true if the evaluated result contains only NO_VALID_SETUP
 * (i.e. evaluation ran but no tradable setup was found).
 */
export function isEvaluatedNoValidSetup(richSetups: RichSetup[]): boolean {
  return richSetups.length === 1 &&
         richSetups[0].setup_classification === 'NO_VALID_SETUP';
}
